import type { NodeSingular } from 'cytoscape';
import type {
  ProcessFuncCodeDetailDto,
  FunctionLanguage,
} from '../../../../api/opmApi';

export interface ProcessDefinitionDrawerProps {
  visible: boolean;
  node: NodeSingular | null;
  onClose: () => void;
  ontologyId: number;
}

export interface SchemaField {
  key: string;
  name: string;
  codeName?: string;
  type: 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object';
  description?: string;
  required?: boolean;
  isArray?: boolean;
  constraints: SchemaConstraints;
  children?: SchemaField[];
  items?: SchemaField;
  sourceType?: string;
  sourceLink?: string;
  hasMappingConfig?: boolean;
}

export interface SchemaConstraints {
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  format?: string;
  enum?: string[];
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number;
  exclusiveMaximum?: number;
  multipleOf?: number;
  minItems?: number;
  maxItems?: number;
  uniqueItems?: boolean;
}

export type StringFormat =
  | 'date'
  | 'date-time'
  | 'email'
  | 'uri'
  | 'uuid'
  | 'ipv4'
  | 'ipv6'
  | 'hostname';

export const STRING_FORMATS: { label: string; value: StringFormat }[] = [
  { label: '日期 (date)', value: 'date' },
  { label: '日期时间 (date-time)', value: 'date-time' },
  { label: '邮箱 (email)', value: 'email' },
  { label: 'URI', value: 'uri' },
  { label: 'UUID', value: 'uuid' },
  { label: 'IPv4', value: 'ipv4' },
  { label: 'IPv6', value: 'ipv6' },
  { label: '主机名 (hostname)', value: 'hostname' },
];

export const FIELD_TYPES: { label: string; value: SchemaField['type'] }[] = [
  { label: 'String', value: 'string' },
  { label: 'Number', value: 'number' },
  { label: 'Integer', value: 'integer' },
  { label: 'Boolean', value: 'boolean' },
  { label: 'Array', value: 'array' },
  { label: 'Object', value: 'object' },
];

/**
 * 将 SchemaField[] 转换为后端期望的 inputSchema 格式（数组格式）
 */
export function fieldsToInputSchema(fields: SchemaField[]): any[] {
  return fields.map(field => buildInputSchemaItem(field));
}

/**
 * 将 SchemaField[] 转换为后端期望的 outputSchema 格式（数组格式，与 inputSchema 保持一致）
 */
export function fieldsToOutputSchema(fields: SchemaField[]): any[] {
  return fields.map(field => buildOutputSchemaItem(field));
}

function buildInputSchemaItem(field: SchemaField): Record<string, any> {
  const item: Record<string, any> = {
    name: field.name,
    codeName: field.codeName || field.name,
    description: field.description || '',
    type: field.type,
    required: field.required || false, // 始终包含 required 属性
    exclusiveMinimum: false,
    exclusiveMaximum: false,
    uniqueItems: false,
    additionalProperties: true,
    sourceType: 'MANUAL',
  };

  // 如果是对象类型，添加properties
  if (field.type === 'object' && field.children && field.children.length > 0) {
    item.properties = {};
    item.ontologyObjectId = 0; // 可以根据需要设置
    item.ontologyObjectName = field.name;
    item.sourceType = 'OBJECT';

    field.children.forEach(child => {
      const childKey = child.codeName || child.name;
      const childProperty: Record<string, any> = {
        name: child.name,
        codeName: childKey,
        description: child.description || '',
        type: child.type,
        required: child.required || false, // 始终包含 required 属性
        exclusiveMinimum: false,
        exclusiveMaximum: false,
        uniqueItems: false,
        additionalProperties: true,
        sourceType: 'ATTRIBUTE',
      };

      item.properties[childKey] = childProperty;

      // 添加约束条件
      addConstraintsToProperty(item.properties[childKey], child.constraints);
    });
  } else {
    // 添加约束条件
    addConstraintsToProperty(item, field.constraints);
  }

  // 对象数组类型：写入 isArray 和数量约束
  if (field.isArray) {
    item.isArray = true;
    if (field.constraints.minItems !== undefined)
      item.minItems = field.constraints.minItems;
    if (field.constraints.maxItems !== undefined)
      item.maxItems = field.constraints.maxItems;
    if (field.constraints.uniqueItems)
      item.uniqueItems = field.constraints.uniqueItems;
  }

  return item;
}

function buildOutputSchemaItem(field: SchemaField): Record<string, any> {
  const item: Record<string, any> = {
    name: field.name,
    codeName: field.codeName || field.name,
    description: field.description || '',
    type: field.type,
    required: field.required || false, // 始终包含 required 属性
    exclusiveMinimum: false,
    exclusiveMaximum: false,
    uniqueItems: false,
    additionalProperties: true,
    sourceType: 'MANUAL',
  };

  // 如果是对象类型，添加properties
  if (field.type === 'object' && field.children && field.children.length > 0) {
    item.properties = {};
    item.ontologyObjectId = 0;
    item.ontologyObjectName = field.name;
    item.sourceType = 'OBJECT';

    field.children.forEach(child => {
      const childKey = child.codeName || child.name;
      item.properties[childKey] = {
        name: child.name,
        codeName: child.codeName || child.name,
        description: child.description || '',
        type: child.type,
        required: child.required || false, // 始终包含 required 属性
        exclusiveMinimum: false,
        exclusiveMaximum: false,
        uniqueItems: false,
        additionalProperties: true,
        sourceType: 'MANUAL',
      };

      // 添加约束条件
      addConstraintsToProperty(
        item.properties[childKey],
        child.constraints,
        'enumValues'
      );
    });
  } else {
    // 添加约束条件
    addConstraintsToProperty(item, field.constraints, 'enumValues');
  }

  // 对象数组类型：写入 isArray 和数量约束
  if (field.isArray) {
    item.isArray = true;
    if (field.constraints.minItems !== undefined)
      item.minItems = field.constraints.minItems;
    if (field.constraints.maxItems !== undefined)
      item.maxItems = field.constraints.maxItems;
    if (field.constraints.uniqueItems)
      item.uniqueItems = field.constraints.uniqueItems;
  }

  return item;
}

function addConstraintsToProperty(
  property: Record<string, any>,
  constraints: SchemaConstraints,
  enumFieldName: 'enum' | 'enumValues' = 'enum'
): void {
  const c = constraints;

  switch (property.type) {
    case 'string':
      if (c.minLength !== undefined) property.minLength = c.minLength;
      if (c.maxLength !== undefined) property.maxLength = c.maxLength;
      if (c.pattern) property.pattern = c.pattern;
      if (c.format) property.format = c.format;
      if (c.enum && c.enum.length > 0) property[enumFieldName] = c.enum;
      break;
    case 'number':
    case 'integer':
      if (c.minimum !== undefined) property.minimum = c.minimum;
      if (c.maximum !== undefined) property.maximum = c.maximum;
      if (c.exclusiveMinimum !== undefined)
        property.exclusiveMinimum = c.exclusiveMinimum;
      if (c.exclusiveMaximum !== undefined)
        property.exclusiveMaximum = c.exclusiveMaximum;
      if (c.multipleOf !== undefined) property.multipleOf = c.multipleOf;
      if (c.enum && c.enum.length > 0) {
        const numericEnum = c.enum.map(Number).filter(n => !isNaN(n));
        if (numericEnum.length > 0) property[enumFieldName] = numericEnum;
      }
      break;
    case 'array':
      if (c.minItems !== undefined) property.minItems = c.minItems;
      if (c.maxItems !== undefined) property.maxItems = c.maxItems;
      if (c.uniqueItems) property.uniqueItems = true;
      break;
  }
}

/**
 * 将 SchemaField[] 转换为 JSON Schema 对象
 */
export function fieldsToJsonSchema(fields: SchemaField[]): Record<string, any> {
  const properties: Record<string, any> = {};
  const required: string[] = [];

  fields.forEach(field => {
    let prop: Record<string, any> = {};

    if (field.isArray) {
      prop.type = 'array';
      prop.items = buildFieldSchema(field);
      if (field.constraints.minItems !== undefined)
        prop.minItems = field.constraints.minItems;
      if (field.constraints.maxItems !== undefined)
        prop.maxItems = field.constraints.maxItems;
      if (field.constraints.uniqueItems) prop.uniqueItems = true;
    } else {
      prop = buildFieldSchema(field);
    }

    if (field.description) prop.description = field.description;
    properties[field.name] = prop;

    if (field.required) {
      required.push(field.name);
    }
  });

  const schema: Record<string, any> = {
    type: 'object',
    properties,
  };
  if (required.length > 0) schema.required = required;

  return schema;
}

function buildFieldSchema(field: SchemaField): Record<string, any> {
  const prop: Record<string, any> = { type: field.type };
  const c = field.constraints;

  switch (field.type) {
    case 'string':
      if (c.minLength !== undefined) prop.minLength = c.minLength;
      if (c.maxLength !== undefined) prop.maxLength = c.maxLength;
      if (c.pattern) prop.pattern = c.pattern;
      if (c.format) prop.format = c.format;
      if (c.enum && c.enum.length > 0) prop.enum = c.enum;
      break;
    case 'number':
    case 'integer':
      if (c.minimum !== undefined) prop.minimum = c.minimum;
      if (c.maximum !== undefined) prop.maximum = c.maximum;
      if (c.exclusiveMinimum !== undefined)
        prop.exclusiveMinimum = c.exclusiveMinimum;
      if (c.exclusiveMaximum !== undefined)
        prop.exclusiveMaximum = c.exclusiveMaximum;
      if (c.multipleOf !== undefined) prop.multipleOf = c.multipleOf;
      if (c.enum && c.enum.length > 0) prop.enum = c.enum.map(Number);
      break;
    case 'boolean':
      break;
    case 'object':
      if (field.children && field.children.length > 0) {
        const nested = fieldsToJsonSchema(field.children);
        prop.properties = nested.properties;
        if (nested.required) prop.required = nested.required;
      }
      break;
    case 'array':
      if (field.items) {
        prop.items = buildFieldSchema(field.items);
      }
      if (c.minItems !== undefined) prop.minItems = c.minItems;
      if (c.maxItems !== undefined) prop.maxItems = c.maxItems;
      if (c.uniqueItems) prop.uniqueItems = true;
      break;
  }

  return prop;
}

/**
 * 解析后端返回的 Schema 数据为 SchemaField[]
 *
 * 后端格式示例（数组，每个元素包含 name/type/required/properties 等）:
 * [
 *   {
 *     "name": "Object99",
 *     "type": "object",
 *     "required": false,
 *     "properties": {
 *       "a": { "name": "a", "type": "string", "required": false, ... },
 *       "b": { "name": "b", "type": "string", "required": false, ... }
 *     }
 *   }
 * ]
 *
 * 也兼容标准 JSON Schema 格式 { type: "object", properties: {...}, required: [...] }
 */
export function parseBackendSchema(schema: any): SchemaField[] {
  if (!schema) {
    return [];
  }

  console.log('[parseBackendSchema] 输入 schema:', schema);

  try {
    // 如果是字符串，尝试解析JSON
    let parsedSchema = schema;
    if (typeof schema === 'string') {
      try {
        parsedSchema = JSON.parse(schema);
      } catch (parseError) {
        console.error('Failed to parse schema JSON string:', parseError);
        return [];
      }
    }

    if (!Array.isArray(parsedSchema)) {
      // 有 name/codeName 的具名对象（如后端 outputSchema 实体），整体作为一个 SchemaField
      // parseBackendSchemaItem 内部会将 properties 解析为 children
      if (parsedSchema.name || parsedSchema.codeName) {
        return [parseBackendSchemaItem(parsedSchema)];
      }

      // 标准 JSON Schema wrapper（无名称，只有 type/properties），展开 properties 作为顶层字段
      if (
        parsedSchema.properties &&
        typeof parsedSchema.properties === 'object'
      ) {
        // 如果顶层对象有 sourceLink 或 sourceType，需要传递给所有子属性
        const parentSourceLink = parsedSchema.sourceLink;
        const parentSourceType = parsedSchema.sourceType;

        return Object.entries(parsedSchema.properties)
          .map(([key, item]: [string, any]) => {
            try {
              if (!item || typeof item !== 'object') {
                console.warn(`Invalid property ${key}:`, item);
                return null;
              }
              // 合并父级和子级的 sourceLink 和 sourceType（子级优先）
              const itemWithDefaults = {
                ...item,
                codeName: item.codeName || key,
                name: item.name || key,
                // 如果子属性没有 sourceLink，使用父级的
                sourceLink:
                  item.sourceLink !== undefined
                    ? item.sourceLink
                    : parentSourceLink,
                sourceType:
                  item.sourceType !== undefined
                    ? item.sourceType
                    : parentSourceType,
              };
              return parseBackendSchemaItem(itemWithDefaults);
            } catch (itemError) {
              console.error(`Error parsing property ${key}:`, itemError);
              return null;
            }
          })
          .filter(Boolean) as SchemaField[];
      }
      // 如果是单个对象但没有properties，可能整个对象就是一个schema项
      if (parsedSchema.type) {
        return [parseBackendSchemaItem(parsedSchema)];
      }
      return [];
    }

    const result = parsedSchema
      .map((item: any, index: number) => {
        try {
          if (!item || typeof item !== 'object') {
            console.warn(`Invalid schema item at index ${index}:`, item);
            return null;
          }
          return parseBackendSchemaItem(item);
        } catch (itemError) {
          console.error(
            `Error parsing schema item at index ${index}:`,
            itemError
          );
          return null;
        }
      })
      .filter(Boolean) as SchemaField[];

    console.log('[parseBackendSchema] 输出结果:', result);
    return result;
  } catch (error) {
    console.error('Failed to parse backend schema:', error);
    return [];
  }
}

/**
 * 解析后端返回的单个 Schema 项
 */
function parseBackendSchemaItem(item: any, parentKey = ''): SchemaField {
  if (!item || typeof item !== 'object') {
    throw new Error(`Invalid schema item: ${JSON.stringify(item)}`);
  }

  const key = parentKey
    ? `${parentKey}.${item.codeName || item.name || 'unknown'}`
    : item.codeName || item.name || 'unknown';

  const field: SchemaField = {
    key,
    name: item.name || '',
    codeName: item.codeName || item.name || '',
    type: item.type || 'string',
    description: item.description || '',
    required: !!item.required,
    isArray: !!item.isArray,
    constraints: {
      ...(item.minLength !== undefined && { minLength: item.minLength }),
      ...(item.maxLength !== undefined && { maxLength: item.maxLength }),
      ...(item.minimum !== undefined && { minimum: item.minimum }),
      ...(item.maximum !== undefined && { maximum: item.maximum }),
      ...(item.multipleOf !== undefined && { multipleOf: item.multipleOf }),
      ...(item.pattern && { pattern: item.pattern }),
      ...(item.format && { format: item.format }),
      ...(item.minItems !== undefined && { minItems: item.minItems }),
      ...(item.maxItems !== undefined && { maxItems: item.maxItems }),
      ...(item.uniqueItems && { uniqueItems: true }),
      ...(item.exclusiveMinimum !== undefined && {
        exclusiveMinimum: item.exclusiveMinimum,
      }),
      ...(item.exclusiveMaximum !== undefined && {
        exclusiveMaximum: item.exclusiveMaximum,
      }),
    },
    sourceType: item.sourceType,
    sourceLink: item.sourceLink,
    hasMappingConfig: item.hasMappingConfig,
  };

  // 处理子属性（对象类型）
  if (
    item.type === 'object' &&
    item.properties &&
    typeof item.properties === 'object'
  ) {
    const children: SchemaField[] = [];
    Object.entries(item.properties).forEach(
      ([childKey, childItem]: [string, any]) => {
        try {
          if (childItem && typeof childItem === 'object') {
            children.push(parseBackendSchemaItem(childItem, key));
          }
        } catch (error) {
          console.warn(`Failed to parse child property ${childKey}:`, error);
        }
      }
    );
    field.children = children;
  }

  return field;
}
/**
 * 将标准 JSON Schema 对象解析为 SchemaField[]（兼容用途）
 */
export function jsonSchemaToFields(
  schema: Record<string, any> | null | undefined
): SchemaField[] {
  if (!schema || !schema.properties) return [];

  const requiredList: string[] = schema.required || [];
  const fields: SchemaField[] = [];

  Object.entries(schema.properties).forEach(
    ([name, propDef]: [string, any]) => {
      const field = parseStdSchemaField(
        name,
        propDef,
        requiredList.includes(name)
      );
      fields.push(field);
    }
  );

  return fields;
}

function parseStdSchemaField(
  name: string,
  prop: Record<string, any>,
  isRequired: boolean
): SchemaField {
  const isArray = prop.type === 'array';
  const actualProp = isArray && prop.items ? prop.items : prop;
  const type = (
    isArray ? actualProp.type || 'string' : prop.type
  ) as SchemaField['type'];

  const constraints: SchemaConstraints = {};

  if (type === 'string' || actualProp.type === 'string') {
    if (actualProp.minLength !== undefined)
      constraints.minLength = actualProp.minLength;
    if (actualProp.maxLength !== undefined)
      constraints.maxLength = actualProp.maxLength;
    if (actualProp.pattern) constraints.pattern = actualProp.pattern;
    if (actualProp.format) constraints.format = actualProp.format;
    if (actualProp.enum || actualProp.enumValues)
      constraints.enum = (actualProp.enum || actualProp.enumValues).map(String);
  }

  if (
    type === 'number' ||
    type === 'integer' ||
    actualProp.type === 'number' ||
    actualProp.type === 'integer'
  ) {
    if (actualProp.minimum !== undefined)
      constraints.minimum = actualProp.minimum;
    if (actualProp.maximum !== undefined)
      constraints.maximum = actualProp.maximum;
    if (actualProp.exclusiveMinimum !== undefined)
      constraints.exclusiveMinimum = actualProp.exclusiveMinimum;
    if (actualProp.exclusiveMaximum !== undefined)
      constraints.exclusiveMaximum = actualProp.exclusiveMaximum;
    if (actualProp.multipleOf !== undefined)
      constraints.multipleOf = actualProp.multipleOf;
    if (actualProp.enum) constraints.enum = actualProp.enum.map(String);
  }

  if (isArray) {
    if (prop.minItems !== undefined) constraints.minItems = prop.minItems;
    if (prop.maxItems !== undefined) constraints.maxItems = prop.maxItems;
    if (prop.uniqueItems) constraints.uniqueItems = prop.uniqueItems;
  }

  const field: SchemaField = {
    key: name,
    name: prop.name || name,
    codeName:
      prop.codeName || (prop.name && prop.name !== name ? name : undefined),
    type,
    description: prop.description,
    required: isRequired,
    isArray,
    constraints,
    sourceType: prop.sourceType,
    sourceLink: prop.sourceLink,
  };

  if (type === 'object' && actualProp.properties) {
    field.children = jsonSchemaToFields(actualProp);
  }

  return field;
}

// =====================================================================
// 根据后端 Schema 生成 TypeScript 类型声明（.d.ts）
// =====================================================================

const SCHEMA_TYPE_TO_TS: Record<string, string> = {
  string: 'string',
  number: 'number',
  integer: 'number',
  boolean: 'boolean',
  object: 'Record<string, any>',
  array: 'any[]',
};

/**
 * 根据后端返回的 inputSchema/outputSchema 和函数名生成 .d.ts 类型声明字符串
 */
export function generateTypeDefsFromDefinition(opts: {
  functionName?: string;
  inputSchema?: any;
  outputSchema?: any;
  processName?: string;
}): string {
  const { functionName, inputSchema, outputSchema, processName } = opts;
  const lines: string[] = [];

  lines.push('// Auto-generated type definitions for Process function');
  lines.push(`// Process: ${processName || 'unknown'}`);
  lines.push('');

  // 解析输入参数
  const inputItems = normalizeSchemaArray(inputSchema);
  const outputItems = normalizeSchemaArray(outputSchema);

  // 为每个输入参数的 object 类型生成 interface
  const inputInterfaces = generateInterfaces(inputItems, 'Input');
  const outputInterfaces = generateInterfaces(outputItems, 'Output');

  if (inputInterfaces.length > 0) {
    lines.push('// ===== 输入参数类型 =====');
    lines.push(...inputInterfaces);
    lines.push('');
  }

  if (outputInterfaces.length > 0) {
    lines.push('// ===== 输出参数类型 =====');
    lines.push(...outputInterfaces);
    lines.push('');
  }

  // 生成函数签名
  const fnName = functionName || 'processFunction';
  const inputParams = buildFunctionParams(inputItems);
  const outputType = buildReturnType(outputItems);

  lines.push('// ===== 函数签名 =====');
  lines.push(`declare function ${fnName}(${inputParams}): ${outputType};`);
  lines.push('');

  return lines.join('\n');
}

/**
 * 将后端 schema 统一转换为数组格式
 */
function normalizeSchemaArray(schema: any): any[] {
  if (!schema) return [];
  if (typeof schema === 'string') {
    try {
      schema = JSON.parse(schema);
    } catch {
      return [];
    }
  }
  if (Array.isArray(schema)) return schema;
  // 兼容单个对象格式（outputSchema 常见）
  if (schema.type === 'object' || schema.properties) {
    return [schema];
  }
  return [];
}

/**
 * 为 schema 项列表生成 TypeScript interface 声明
 */
function generateInterfaces(items: any[], prefix: string): string[] {
  const lines: string[] = [];

  for (const item of items) {
    if (item.type === 'object' || item.properties) {
      const interfaceName = toInterfaceName(
        item.codeName ||
          item.ontologyObjectName ||
          item.name ||
          `${prefix}Param`
      );
      lines.push(
        ...generateSingleInterface(
          interfaceName,
          item.properties || {},
          item.requiredProperties
        )
      );
      lines.push('');

      // 递归：子属性中如果也有 object 类型，也生成 interface
      for (const [propKey, propDef] of Object.entries(
        item.properties || {}
      ) as [string, any][]) {
        if (propDef.type === 'object' && propDef.properties) {
          const childName = toInterfaceName(
            propDef.ontologyObjectName ||
              propDef.codeName ||
              propDef.name ||
              propKey
          );
          lines.push(
            ...generateSingleInterface(
              childName,
              propDef.properties,
              propDef.requiredProperties
            )
          );
          lines.push('');
        }
      }
    }
  }

  return lines;
}

/**
 * 为 schema 属性生成 JSDoc 约束注释行
 */
function buildConstraintJsDoc(propDef: any): string[] {
  const tags: string[] = [];
  if (propDef.description) tags.push(` * ${propDef.description}`);
  if (propDef.minLength !== undefined || propDef.maxLength !== undefined) {
    const parts: string[] = [];
    if (propDef.minLength !== undefined)
      parts.push(`min: ${propDef.minLength}`);
    if (propDef.maxLength !== undefined)
      parts.push(`max: ${propDef.maxLength}`);
    tags.push(` * @length ${parts.join(', ')}`);
  }
  if (propDef.minimum !== undefined || propDef.maximum !== undefined) {
    const parts: string[] = [];
    if (propDef.minimum !== undefined) parts.push(`min: ${propDef.minimum}`);
    if (propDef.maximum !== undefined) parts.push(`max: ${propDef.maximum}`);
    tags.push(` * @range ${parts.join(', ')}`);
  }
  if (propDef.pattern) tags.push(` * @pattern ${propDef.pattern}`);
  if (propDef.format) tags.push(` * @format ${propDef.format}`);
  if (propDef.minItems !== undefined || propDef.maxItems !== undefined) {
    const parts: string[] = [];
    if (propDef.minItems !== undefined) parts.push(`min: ${propDef.minItems}`);
    if (propDef.maxItems !== undefined) parts.push(`max: ${propDef.maxItems}`);
    tags.push(` * @items ${parts.join(', ')}`);
  }
  if (propDef.uniqueItems) tags.push(` * @uniqueItems true`);
  if (tags.length === 0) return [];
  return ['/**', ...tags, ' */'];
}

/**
 * 生成单个 interface
 */
function generateSingleInterface(
  name: string,
  properties: Record<string, any>,
  requiredProps?: string[]
): string[] {
  const lines: string[] = [];
  const requiredSet = new Set(requiredProps || []);

  lines.push(`interface ${name} {`);

  for (const [propKey, propDef] of Object.entries(properties) as [
    string,
    any,
  ][]) {
    const fieldName = propDef.codeName || propKey;
    const isRequired = propDef.required === true || requiredSet.has(propKey);
    const tsType = schemaItemToTsType(fieldName, propDef);
    const optional = isRequired ? '' : '?';
    const nameComment =
      propDef.name && propDef.name !== fieldName ? ` // ${propDef.name}` : '';
    lines.push(`  ${fieldName}${optional}: ${tsType};${nameComment}`);
  }

  lines.push('}');
  return lines;
}

/**
 * 将单个 schema 属性转换为 TypeScript 类型字符串
 */
function schemaItemToTsType(name: string, item: any): string {
  if (!item) return 'any';

  const type = item.type || 'string';
  let baseType: string;

  switch (type) {
    case 'string': {
      if (item.enum || item.enumValues) {
        const vals = item.enum || item.enumValues;
        baseType = vals.map((v: any) => `'${v}'`).join(' | ');
      } else {
        baseType = 'string';
      }
      break;
    }
    case 'number':
    case 'integer':
      baseType = 'number';
      break;
    case 'boolean':
      baseType = 'boolean';
      break;
    case 'object': {
      baseType = item.properties
        ? toInterfaceName(
            item.codeName || item.ontologyObjectName || item.name || name
          )
        : 'Record<string, any>';
      break;
    }
    case 'array': {
      baseType = item.items
        ? `${schemaItemToTsType(name + 'Item', item.items)}[]`
        : 'any[]';
      break;
    }
    default:
      baseType = SCHEMA_TYPE_TO_TS[type] || 'any';
  }

  return item.isArray && type !== 'array' ? `${baseType}[]` : baseType;
}

/**
 * 构建函数参数列表
 */
function buildFunctionParams(inputItems: any[]): string {
  if (inputItems.length === 0) return '';

  const params: string[] = [];
  for (const item of inputItems) {
    const name = item.codeName || toCamelCase(item.name || 'input');
    const baseType =
      item.type === 'object' || item.properties
        ? toInterfaceName(
            item.codeName ||
              item.ontologyObjectName ||
              item.name ||
              'InputParam'
          )
        : SCHEMA_TYPE_TO_TS[item.type] || 'any';
    const tsType = item.isArray ? `${baseType}[]` : baseType;
    const optional = item.required === true ? '' : '?';
    params.push(`${name}${optional}: ${tsType}`);
  }

  return params.join(', ');
}

/**
 * 构建返回值类型
 */
function buildReturnType(outputItems: any[]): string {
  if (outputItems.length === 0) return 'void';

  if (outputItems.length === 1) {
    const item = outputItems[0];
    const baseType =
      item.type === 'object' || item.properties
        ? toInterfaceName(
            item.codeName ||
              item.ontologyObjectName ||
              item.name ||
              'OutputResult'
          )
        : SCHEMA_TYPE_TO_TS[item.type] || 'any';
    const tsType = item.isArray ? `${baseType}[]` : baseType;
    return tsType;
  }

  // 多个输出，构建 object
  const parts = outputItems.map(item => {
    const name = item.codeName || toCamelCase(item.name || 'result');
    const baseType =
      item.type === 'object' || item.properties
        ? toInterfaceName(
            item.codeName ||
              item.ontologyObjectName ||
              item.name ||
              'OutputResult'
          )
        : SCHEMA_TYPE_TO_TS[item.type] || 'any';
    const tsType = item.isArray ? `${baseType}[]` : baseType;
    return `${name}: ${tsType}`;
  });

  return `{ ${parts.join('; ')} }`;
}

/**
 * 转换为 PascalCase interface 名
 */
function toInterfaceName(name: string): string {
  return (
    name.charAt(0).toUpperCase() + name.slice(1).replace(/[^a-zA-Z0-9]/g, '')
  );
}

/**
 * 转换为 camelCase 参数名
 */
function toCamelCase(name: string): string {
  return (
    name.charAt(0).toLowerCase() + name.slice(1).replace(/[^a-zA-Z0-9]/g, '')
  );
}

/**
 * 将 schema 统一转为数组，兼容数组格式和单个对象格式
 */
function normalizeSchemaToItems(schema: any): any[] {
  if (!schema) return [];
  if (typeof schema === 'string') {
    try {
      schema = JSON.parse(schema);
    } catch {
      return [];
    }
  }
  if (Array.isArray(schema)) return schema;
  // 单个对象 schema（outputSchema 常见格式）
  if (schema.type === 'object' || schema.properties) return [schema];
  return [];
}

/**
 * 获取 schema 项的标识符名称（优先 codeName，其次 name，最后 fallback）
 */
function getItemCodeName(item: any, fallback: string): string {
  return item.codeName || item.name || fallback;
}

/**
 * 获取 schema 项的接口类型名（PascalCase codeName 或 ontologyObjectName）
 */
function getItemInterfaceName(item: any, fallback: string): string {
  const base =
    item.codeName || item.ontologyObjectName || item.name || fallback;
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/**
 * 根据 properties 生成 TypeScript interface 定义块
 */
function buildTsInterface(interfaceName: string, item: any): string[] {
  const lines: string[] = [];
  lines.push(`interface ${interfaceName} {`);
  if (item.properties && typeof item.properties === 'object') {
    for (const [propKey, propDef] of Object.entries(item.properties) as [
      string,
      any,
    ][]) {
      const fieldName = propDef.codeName || propKey;
      const enumVals = propDef.enum || propDef.enumValues;
      let tsType: string;
      if (enumVals && Array.isArray(enumVals) && enumVals.length > 0) {
        const isNum = propDef.type === 'number' || propDef.type === 'integer';
        tsType = enumVals
          .map((v: any) => (isNum ? String(v) : `'${v}'`))
          .join(' | ');
      } else {
        tsType = primitiveToTsType(propDef.type || 'string');
      }
      if (propDef.isArray) tsType = `${tsType}[]`;
      const optional = propDef.required === true ? '' : '?';
      const comment =
        propDef.name && propDef.name !== fieldName ? ` // ${propDef.name}` : '';
      lines.push(`  ${fieldName}${optional}: ${tsType};${comment}`);
    }
  }
  lines.push('}');
  return lines;
}

/**
 * 根据 properties 生成 Python TypedDict 定义块
 */
function buildPyTypedDict(className: string, item: any): string[] {
  const lines: string[] = [];
  lines.push(`class ${className}(TypedDict, total=False):`);
  if (item.properties && typeof item.properties === 'object') {
    for (const [propKey, propDef] of Object.entries(item.properties) as [
      string,
      any,
    ][]) {
      const fieldName = toSnakeCase(propDef.codeName || propKey);
      const baseType =
        primitiveToTsType(propDef.type || 'string') === 'any'
          ? 'Any'
          : primitiveToPyType(propDef.type || 'string');
      const pyType = propDef.isArray ? `List[${baseType}]` : baseType;
      const comment =
        propDef.name && propDef.name !== (propDef.codeName || propKey)
          ? `  # ${propDef.name}`
          : '';
      lines.push(`    ${fieldName}: ${pyType}${comment}`);
    }
  } else {
    lines.push('    pass');
  }
  return lines;
}

function primitiveToTsType(type: string): string {
  switch (type) {
    case 'string':
      return 'string';
    case 'number':
      return 'number';
    case 'integer':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'array':
      return 'any[]';
    case 'object':
      return 'Record<string, any>';
    default:
      return 'any';
  }
}

function primitiveToPyType(type: string): string {
  switch (type) {
    case 'string':
      return 'str';
    case 'number':
      return 'float';
    case 'integer':
      return 'int';
    case 'boolean':
      return 'bool';
    case 'array':
      return 'list';
    case 'object':
      return 'dict';
    default:
      return 'Any';
  }
}

/**
 * 根据函数名、输入输出 Schema 和语言生成函数签名代码
 */
export function generateFunctionSignature(opts: {
  functionName?: string;
  inputSchema?: any;
  outputSchema?: any;
  language: FunctionLanguage;
}): string {
  const { functionName, inputSchema, outputSchema, language } = opts;
  const fnName = functionName || 'processFunction';
  const inputItems = normalizeSchemaToItems(inputSchema);
  const outputItems = normalizeSchemaToItems(outputSchema);

  if (language === 'PYTHON') {
    return generatePythonSignature(fnName, inputItems, outputItems);
  }
  return generateTypeScriptSignature(fnName, inputItems, outputItems);
}

function generateTypeScriptSignature(
  fnName: string,
  inputItems: any[],
  outputItems: any[]
): string {
  const lines: string[] = [];

  // 生成输入参数接口定义
  const inputParams: string[] = [];
  inputItems.forEach((item, idx) => {
    const paramName = getItemCodeName(item, `input${idx}`);
    const optional = item.required === true ? '' : '?';
    const arraySuffix = item.isArray ? '[]' : '';
    if (item.type === 'object' || item.properties) {
      const interfaceName = getItemInterfaceName(item, `Input${idx}`);
      lines.push(...buildTsInterface(interfaceName, item));
      lines.push('');
      inputParams.push(
        `${paramName}${optional}: ${interfaceName}${arraySuffix}`
      );
    } else {
      const tsType = primitiveToTsType(item.type || 'string');
      inputParams.push(`${paramName}${optional}: ${tsType}${arraySuffix}`);
    }
  });

  // 生成输出类型接口定义
  let returnType = 'void';
  if (outputItems.length === 1) {
    const outItem = outputItems[0];
    const arraySuffix = outItem.isArray ? '[]' : '';
    if (outItem.type === 'object' || outItem.properties) {
      const interfaceName = getItemInterfaceName(outItem, 'Output');
      lines.push(...buildTsInterface(interfaceName, outItem));
      lines.push('');
      returnType = `${interfaceName}${arraySuffix}`;
    } else {
      returnType = `${primitiveToTsType(outItem.type || 'string')}${arraySuffix}`;
    }
  } else if (outputItems.length > 1) {
    const parts = outputItems.map((item, idx) => {
      const name = getItemCodeName(item, `result${idx}`);
      const arraySuffix = item.isArray ? '[]' : '';
      if (item.type === 'object' || item.properties) {
        const interfaceName = getItemInterfaceName(item, `Output${idx}`);
        lines.push(...buildTsInterface(interfaceName, item));
        lines.push('');
        return `${name}: ${interfaceName}${arraySuffix}`;
      }
      return `${name}: ${primitiveToTsType(item.type || 'string')}${arraySuffix}`;
    });
    returnType = `{ ${parts.join('; ')} }`;
  }

  lines.push(`function ${fnName}(${inputParams.join(', ')}): ${returnType} {`);
  lines.push(`  // TODO: implement`);
  lines.push(`}`);
  return lines.join('\n');
}

function generatePythonSignature(
  fnName: string,
  inputItems: any[],
  outputItems: any[]
): string {
  const lines: string[] = [];
  lines.push('from typing import Any, List, Optional');
  lines.push('from typing_extensions import TypedDict');
  lines.push('');

  // 生成输入 TypedDict 定义
  const inputParams: string[] = ['self'];
  inputItems.forEach((item, idx) => {
    const paramName = toSnakeCase(getItemCodeName(item, `input${idx}`));
    if (item.type === 'object' || item.properties) {
      const className = getItemInterfaceName(item, `Input${idx}`);
      lines.push(...buildPyTypedDict(className, item));
      lines.push('');
      const baseType = item.isArray ? `List[${className}]` : className;
      const optional = item.required === true ? '' : 'Optional[';
      const optClose = item.required === true ? '' : ']';
      inputParams.push(`${paramName}: ${optional}${baseType}${optClose}`);
    } else {
      const baseType = primitiveToPyType(item.type || 'string');
      const pyType = item.isArray ? `List[${baseType}]` : baseType;
      inputParams.push(`${paramName}: ${pyType}`);
    }
  });

  // 生成输出 TypedDict 定义
  let retType = 'None';
  if (outputItems.length === 1) {
    const outItem = outputItems[0];
    if (outItem.type === 'object' || outItem.properties) {
      const className = getItemInterfaceName(outItem, 'Output');
      lines.push(...buildPyTypedDict(className, outItem));
      lines.push('');
      retType = outItem.isArray ? `List[${className}]` : className;
    } else {
      const baseType = primitiveToPyType(outItem.type || 'string');
      retType = outItem.isArray ? `List[${baseType}]` : baseType;
    }
  } else if (outputItems.length > 1) {
    const types = outputItems.map((item, idx) => {
      if (item.type === 'object' || item.properties) {
        const className = getItemInterfaceName(item, `Output${idx}`);
        lines.push(...buildPyTypedDict(className, item));
        lines.push('');
        return item.isArray ? `List[${className}]` : className;
      }
      const baseType = primitiveToPyType(item.type || 'string');
      return item.isArray ? `List[${baseType}]` : baseType;
    });
    retType = `tuple[${types.join(', ')}]`;
  }

  lines.push(`def ${fnName}(${inputParams.join(', ')}) -> ${retType}:`);
  lines.push(`    # TODO: implement`);
  lines.push(`    pass`);
  return lines.join('\n');
}

function toSnakeCase(name: string): string {
  return name
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_/, '')
    .replace(/[^a-z0-9_]/g, '_');
}
